<script setup>
/**
 * GviaPaymentDetails
 * Step 2: Invoice selection and payment for payment collection
 * After selecting invoices, opens payment method modal (credit / cash / checks)
 */
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import AppButton from '@/ui/components/AppButton.vue'
import GviaPaymentMethodModal from './GviaPaymentMethodModal.vue'
import GviaSignatureModal from './GviaSignatureModal.vue'
import { useGvia } from '@/core/composables/useGvia'
import { useAuthStore } from '@/app/store/auth'
import { useToast } from '@/core/composables/useToast'
import { sendManualGviaAlert } from '@/core/services/manualGviaAlert'
import { getWalletAgentId } from '@/core/services/wallet'
const authStore = useAuthStore()
const { error: toastError } = useToast()
const {
  clientData,
  loading,
  paymentAmount,
  remainingBalance,
  allSelected,
  hasInvoices,
  togglePaymentSelection,
  toggleSelectAll,
  generatePaymentLink,
  goToSelect,
  formatDate,
  formatCurrency,
} = useGvia()

// Check if user is admin or agent
const { isAdmin, isAgent } = storeToRefs(authStore)

// Manual amount state — auto-open when the client has no open invoices
// (upfront payment: manual amount is the only way to collect)
const showManualAmount = ref(!hasInvoices.value)
const manualAmount = ref('')

// Modal state
const showPaymentMethodModal = ref(false)
const modalOverrideAmount = ref(0)
const showSignatureModal = ref(false)
const pendingAllocation = ref(null)

// Open payment method selection modal
function handleProceedToPayment() {
  if (paymentAmount.value <= 0) {
    toastError('עליך לבחור לפחות חשבונית אחת')
    return
  }
  modalOverrideAmount.value = 0
  showPaymentMethodModal.value = true
}

// Handle credit card selection from modal
async function handleCreditSelected(note = '') {
  showPaymentMethodModal.value = false
  const isManual = modalOverrideAmount.value > 0
  try {
    const paymentLink = await generatePaymentLink(
      isManual ? modalOverrideAmount.value : null,
      isManual,
      note
    )
    window.open(paymentLink, '_self')
  } catch (err) {
    toastError(err.message || 'שגיאה ביצירת קישור תשלום')
  }
}

// Handle finish payment from method modal — open signature modal
function handleFinishPayment(allocation) {
  pendingAllocation.value = allocation
  showPaymentMethodModal.value = false
  showSignatureModal.value = true
}

// Handle manual amount — open modal with override amount (no invoices)
function handleManualPayment() {
  const amount = parseFloat(manualAmount.value)

  if (!amount || amount <= 0) {
    toastError('עליך להזין סכום תקין')
    return
  }

  // Clear invoice selections since this is manual
  toggleSelectAll(false)
  modalOverrideAmount.value = amount
  showPaymentMethodModal.value = true
}

// Toggle manual amount input
function toggleManualInput() {
  showManualAmount.value = !showManualAmount.value
  if (showManualAmount.value) {
    manualAmount.value = ''
  }
}

// Background WhatsApp alert when the agent enters a manual amount.
// Fires on every blur with a valid amount; must never disturb the user
// (no toast, no loader) — failures go to the console only.
async function handleManualAmountBlur() {
  const amount = parseFloat(manualAmount.value)
  if (!amount || amount <= 0) return
  if (!clientData.value) return

  try {
    // Same agent-number resolution as GviaSignatureModal: admins read it from wallet metadata
    let agentNumber = authStore.userAgentId || ''
    if (!agentNumber && authStore.isAdmin) {
      agentNumber = (await getWalletAgentId(authStore.userId)) || ''
    }

    await sendManualGviaAlert({
      agentName: authStore.userDisplayName || '',
      agentNumber,
      clientName: clientData.value.clientName || '',
      clientNum: clientData.value.clientNum || '',
      selectedAmount: paymentAmount.value,
      totalDebt: Number((paymentAmount.value + remainingBalance.value).toFixed(2)),
      manualAmount: amount,
    })
  } catch (err) {
    console.error('Manual gvia WhatsApp alert failed:', err)
  }
}

// Get payment amount display (handles negative)
function getPaymentDisplay(payment) {
  const amount = payment.SUMDEBIT - payment.SUMCREDIT
  return amount
}

// Check if amount is negative (credit)
function isCredit(payment) {
  return payment.SUMDEBIT - payment.SUMCREDIT < 0
}

// Calculate days remaining until due date
function daysToCollection(dateStr) {
  if (!dateStr) return null
  const parts = dateStr.includes('/') ? dateStr.split('/') : null
  let due
  if (parts && parts.length === 3) {
    due = new Date(parts[2], parts[1] - 1, parts[0])
  } else {
    due = new Date(dateStr)
  }
  if (isNaN(due.getTime())) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  due.setHours(0, 0, 0, 0)
  return Math.ceil((due - today) / (1000 * 60 * 60 * 24))
}

// CSS class for days-to-collection value
function daysClass(days) {
  if (days === null) return ''
  if (days < 0) return 'days-overdue'
  if (days === 0) return 'days-warning'
  return 'days-ok'
}
</script>

<template>
  <div class="payment-details">
    <!-- Header with client info -->
    <div class="details-header">
      <button class="back-btn" @click="goToSelect">
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
      <div class="header-info">
        <h3>{{ clientData?.clientName }}</h3>
        <span>לקוח: {{ clientData?.clientNum }}</span>
      </div>
      <div class="balance-badge">
        <span class="balance-label">יתרה:</span>
        <span class="balance-value">{{ formatCurrency(remainingBalance) }}</span>
      </div>
    </div>

    <!-- Payment Summary -->
    <div class="payment-summary">
      <div class="summary-row">
        <span>סכום לתשלום:</span>
        <span v-if="paymentAmount <= 0" class="no-amount">אין סכום לתשלום</span>
        <span v-else class="amount">{{ formatCurrency(paymentAmount) }}</span>
      </div>

      <!-- Manual Amount Button (Admin/Agent only; hidden when manual is the only path) -->
      <button
        v-if="(isAdmin || isAgent) && hasInvoices"
        class="manual-amount-btn"
        @click="toggleManualInput"
      >
        {{ showManualAmount ? 'בטל סכום ידני' : 'הקש סכום ידני' }}
      </button>
    </div>

    <!-- Manual Amount Input (Admin/Agent only) -->
    <Transition name="fade">
      <div v-if="showManualAmount && (isAdmin || isAgent)" class="manual-input-section">
        <input
          v-model="manualAmount"
          type="number"
          class="manual-input"
          placeholder="הזן סכום"
          min="0"
          step="0.01"
          @blur="handleManualAmountBlur"
        />
        <AppButton
          variant="primary"
          :loading="loading"
          :disabled="!manualAmount || parseFloat(manualAmount) <= 0"
          @click="handleManualPayment"
        >
          עבור לתשלום
        </AppButton>
      </div>
    </Transition>

    <!-- No open invoices: upfront payment via manual amount -->
    <div v-if="!hasInvoices" class="no-invoices-state">
      <svg
        viewBox="0 0 24 24"
        width="36"
        height="36"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <rect x="2" y="6" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
      </svg>
      <p class="no-invoices-title">אין חשבוניות פתוחות ללקוח זה</p>
      <p class="no-invoices-sub">ניתן לגבות תשלום מקדמה באמצעות סכום ידני</p>
    </div>

    <!-- Select All Toggle -->
    <div v-if="hasInvoices" class="select-all-row" @click="toggleSelectAll()">
      <div class="checkbox" :class="{ checked: allSelected }">
        <svg
          v-if="allSelected"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <span>בחר הכל</span>
    </div>

    <!-- Payments List -->
    <div class="payments-list">
      <div
        v-for="payment in clientData?.paymentsData"
        :key="payment.IVNUM || payment.FNCIREF1"
        class="payment-row"
        :class="{ selected: payment.checked, credit: isCredit(payment) }"
        @click="togglePaymentSelection(payment)"
      >
        <div class="checkbox" :class="{ checked: payment.checked }">
          <svg
            v-if="payment.checked"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div class="payment-info">
          <div class="payment-number">{{ payment.IVNUM }}</div>
          <div class="payment-dates">
            <span
              >תאריך: <strong>{{ formatDate(payment.BALDATE) }}</strong></span
            >
            <span
              >ת.פרעון: <strong class="due-date">{{ formatDate(payment.FNCDATE) }}</strong></span
            >
            <span v-if="daysToCollection(payment.FNCDATE) !== null"
              >ימים לגבייה:
              <strong :class="daysClass(daysToCollection(payment.FNCDATE))">{{
                daysToCollection(payment.FNCDATE)
              }}</strong></span
            >
          </div>
          <div v-if="payment.DETAILS" class="payment-details-text">{{ payment.DETAILS }}</div>
          <div
            v-if="
              daysToCollection(payment.FNCDATE) !== null && daysToCollection(payment.FNCDATE) < 0
            "
            class="overdue-banner"
          >
            תשלום בחריגת זמן
          </div>
        </div>
        <div class="payment-amount" :class="{ credit: isCredit(payment) }">
          {{ formatCurrency(getPaymentDisplay(payment)) }}
        </div>
      </div>
    </div>

    <!-- Proceed Button -->
    <AppButton
      v-if="!showManualAmount"
      variant="primary"
      size="lg"
      :loading="loading"
      :disabled="paymentAmount <= 0"
      class="proceed-btn"
      @click="handleProceedToPayment"
    >
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
      המשך לתשלום
    </AppButton>

    <!-- Payment Method Selection Modal -->
    <GviaPaymentMethodModal
      v-if="showPaymentMethodModal"
      :override-amount="modalOverrideAmount"
      @close="showPaymentMethodModal = false"
      @select-credit="handleCreditSelected"
      @finish-payment="handleFinishPayment"
    />

    <!-- Signature Modal -->
    <GviaSignatureModal
      v-if="showSignatureModal && pendingAllocation"
      :allocation="pendingAllocation"
      @close="showSignatureModal = false"
    />
  </div>
</template>

<style scoped>
.payment-details {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
}

/* Header */
.details-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--color-primary);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.back-btn:hover {
  background: var(--color-primary-dark);
}

.back-btn svg {
  transform: none;
}

.header-info {
  flex: 1;
}

.header-info h3 {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.header-info span {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.balance-badge {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--bg-tertiary);
  border-radius: var(--border-radius);
}

.balance-label {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.balance-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

/* Select All */
.select-all-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
}

.select-all-row:hover {
  background: var(--bg-tertiary);
}

.select-all-row span {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

/* Checkbox */
.checkbox {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.checkbox.checked {
  background: var(--color-success);
  border-color: var(--color-success);
  color: white;
}

/* Payments List */
.payments-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* No invoices (upfront payment) state */
.no-invoices-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xl) var(--spacing-md);
  color: var(--text-muted);
  flex-shrink: 0;
}

.no-invoices-title {
  margin: var(--spacing-xs) 0 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}

.no-invoices-sub {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.payment-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.payment-row:hover {
  background: var(--bg-secondary);
}

.payment-row.selected {
  background: rgba(34, 197, 94, 0.05);
}

.payment-info {
  flex: 1;
  min-width: 0;
}

.payment-number {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin-bottom: 2px;
}

.payment-dates {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.payment-dates strong {
  color: var(--color-primary);
}

.payment-dates .due-date {
  color: var(--color-error);
}

.days-ok {
  color: var(--color-success);
}

.days-warning {
  color: var(--color-warning);
}

.days-overdue {
  color: var(--color-error);
}

.payment-details-text {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin-top: 4px;
}

.overdue-banner {
  margin-top: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  border-radius: var(--border-radius-sm);
  width: 100%;
}

.payment-amount {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--color-error);
  white-space: nowrap;
}

.payment-amount.credit {
  color: var(--color-success);
}

.payment-row.credit {
  background: rgba(34, 197, 94, 0.03);
}

/* Payment Summary */
.payment-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.summary-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
}

.summary-row .no-amount {
  color: var(--color-error);
}

.summary-row .amount {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-error);
}

.manual-amount-btn {
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-primary);
  border: none;
  border-radius: var(--border-radius-sm);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.manual-amount-btn:hover {
  background: var(--color-primary-dark);
}

/* Manual Input Section */
.manual-input-section {
  display: flex;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.manual-input {
  flex: 1;
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  text-align: center;
}

.manual-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

/* Hide number input spinners */
.manual-input::-webkit-outer-spin-button,
.manual-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.manual-input[type='number'] {
  -moz-appearance: textfield;
}

/* Proceed Button */
.proceed-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  width: 100%;
  border-radius: 0;
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

.proceed-btn:disabled {
  background: var(--bg-tertiary);
  color: var(--text-muted);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 480px) {
  .payment-dates {
    flex-direction: column;
    gap: var(--spacing-xs);
  }

  .balance-badge {
    display: none;
  }
}
</style>
